import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { 
  MapPin, 
  Phone, 
  Mail, 
  Clock,
  Send,
  CheckCircle,
  FileText,
  ArrowRight
} from "lucide-react";
import { Link } from "react-router-dom";
import { getFullAddress } from "@/lib/site-config";
import { useSiteConfig } from "@/lib/useSiteConfig";
import { toast } from "sonner";
import { contactService } from "@/services/contactService";
import { useQuery } from "@tanstack/react-query";
import { formBuilderService } from "@/services/formBuilderService";
import { FormRenderer } from "@/components/forms/FormRenderer";
import { useSiteSettings } from "@/hooks/useSiteSettings";

const Contact = () => {
  const config = useSiteConfig();
  const { organizationDetails } = useSiteSettings();
  
  // Check if a form builder form exists with slug "contact"
  const { data: contactForm } = useQuery({
    queryKey: ['form', 'contact'],
    queryFn: async () => {
      const result = await formBuilderService.getFormBySlug('contact');
      return result.success ? result.data : null;
    },
    staleTime: 5 * 60 * 1000,
  });

  // Get additional offices if available
  const additionalOffices = (organizationDetails?.contact as any)?.additionalOffices || [];

  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    subject: "",
    message: ""
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      const result = await contactService.submitContactForm({
        name: formData.name,
        email: formData.email,
        phone: formData.phone || undefined,
        subject: formData.subject,
        message: formData.message,
      });

      if (result.success) {
        setIsSubmitted(true);
        toast.success(`Message sent successfully! Submission ID: ${result.data.id}. We'll get back to you soon.`);
        
        // Reset form after delay
        setTimeout(() => {
          setFormData({ name: "", email: "", phone: "", subject: "", message: "" });
          setIsSubmitted(false);
        }, 5000);
      } else {
        toast.error(result.error.getUserMessage() || "Failed to send message. Please try again.");
      }
    } catch (error) {
      console.error('Error submitting contact form:', error);
      toast.error("An unexpected error occurred. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  const contactInfo = [
    {
      icon: MapPin,
      title: "Address",
      content: getFullAddress(config),
      action: `https://maps.google.com/?q=${encodeURIComponent(getFullAddress(config))}`
    },
    {
      icon: Phone,
      title: "Phone",
      content: config.contact.phone,
      action: `tel:${config.contact.phone}`
    },
    {
      icon: Mail,
      title: "Email",
      content: config.contact.email,
      action: `mailto:${config.contact.email}`
    },
    {
      icon: Clock,
      title: "Hours",
      content: config.contact.hoursOfOperation || "Monday - Friday: 9am - 5pm",
      action: null
    }
  ];

  return (
    <div className="min-h-screen bg-gradient-warm">
      {/* Hero Section */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-gradient-to-br from-primary/10 via-heritage/5 to-transparent">
        <div className="max-w-7xl mx-auto text-center">
          <h1 className="text-4xl md:text-5xl font-bold text-foreground mb-6">
            Contact <span className="text-heritage">Us</span>
          </h1>
          <p className="text-xl text-muted-foreground max-w-3xl mx-auto">
            We'd love to hear from you. Reach out with questions, feedback, 
            or just to say hello. We're here to help.
          </p>
        </div>
      </section>

      {/* Contact Info & Form */}
      <section className="py-16 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-12">
            {/* Contact Information */}
            <div className="lg:col-span-1">
              <h2 className="text-2xl font-bold text-foreground mb-6">Get in Touch</h2>
              <p className="text-muted-foreground mb-8">
                Have questions or want to learn more? We're here to help and would love to hear from you.
              </p>
              
              {/* Quote Request CTA */}
              <div className="mb-8 p-6 bg-gradient-to-br from-primary/10 to-heritage/10 rounded-lg border border-primary/20">
                <h3 className="font-semibold text-foreground mb-2">Need a Quote?</h3>
                <p className="text-sm text-muted-foreground mb-4">
                  Get a free estimate for your construction project.
                </p>
                <Button asChild variant="outline" className="w-full group">
                  <Link to="/form/quote-request">
                    <FileText className="w-4 h-4 mr-2" />
                    Request a Quote
                    <ArrowRight className="w-4 h-4 ml-2 group-hover:translate-x-1 transition-transform" />
                  </Link>
                </Button>
              </div>

              <div className="space-y-6">
                {contactInfo.map((info, index) => (
                  <div key={index} className="flex items-start gap-4">
                    <div className="w-12 h-12 bg-heritage/10 rounded-full flex items-center justify-center flex-shrink-0">
                      <info.icon className="w-5 h-5 text-heritage" />
                    </div>
                    <div>
                      <h3 className="font-semibold text-foreground">{info.title}</h3>
                      {info.action ? (
                        <a 
                          href={info.action}
                          target={info.title === "Address" ? "_blank" : undefined}
                          rel={info.title === "Address" ? "noopener noreferrer" : undefined}
                          className="text-muted-foreground hover:text-heritage transition-colors"
                        >
                          {info.content}
                        </a>
                      ) : (
                        <p className="text-muted-foreground">{info.content}</p>
                      )}
                    </div>
                  </div>
                ))}
                
                {/* Additional Offices */}
                {additionalOffices.length > 0 && (
                  <div className="pt-6 border-t border-primary-foreground/10">
                    <h3 className="font-semibold text-foreground mb-4">Additional Locations</h3>
                    <div className="space-y-4">
                      {additionalOffices.map((office: any, idx: number) => (
                        <div key={idx} className="space-y-2">
                          <h4 className="font-medium text-foreground">{office.name}</h4>
                          <div className="text-sm text-muted-foreground space-y-1">
                            <p>{office.address?.street}</p>
                            <p>{office.address?.city}, {office.address?.state} {office.address?.zip}</p>
                            {office.phone && (
                              <p>
                                <a href={`tel:${office.phone}`} className="hover:text-heritage transition-colors">
                                  Tel: {office.phone}
                                </a>
                              </p>
                            )}
                            {office.fax && <p>Fax: {office.fax}</p>}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Contact Form */}
            <div className="lg:col-span-2">
              <Card className="shadow-warm">
                <CardHeader>
                  <CardTitle>Send Us a Message</CardTitle>
                </CardHeader>
                <CardContent>
                  {contactForm ? (
                    // Use form builder form if it exists
                    <FormRenderer form={contactForm} />
                  ) : isSubmitted ? (
                    <div className="text-center py-12">
                      <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                        <CheckCircle className="w-8 h-8 text-green-600" />
                      </div>
                      <h3 className="text-xl font-bold text-foreground mb-2">Message Sent!</h3>
                      <p className="text-muted-foreground">
                        Thank you for reaching out. We'll get back to you as soon as possible.
                      </p>
                    </div>
                  ) : (
                    <form onSubmit={handleSubmit} className="space-y-6">
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        <div className="space-y-2">
                          <Label htmlFor="name">Full Name *</Label>
                          <Input
                            id="name"
                            name="name"
                            value={formData.name}
                            onChange={handleChange}
                            placeholder="Your name"
                            required
                            aria-required="true"
                          />
                        </div>
                        <div className="space-y-2">
                          <Label htmlFor="email">Email Address *</Label>
                          <Input
                            id="email"
                            name="email"
                            type="email"
                            value={formData.email}
                            onChange={handleChange}
                            placeholder="your@email.com"
                            required
                            aria-required="true"
                          />
                        </div>
                      </div>

                      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        <div className="space-y-2">
                          <Label htmlFor="phone">Phone Number</Label>
                          <Input
                            id="phone"
                            name="phone"
                            type="tel"
                            value={formData.phone}
                            onChange={handleChange}
                            placeholder="(555) 123-4567"
                          />
                        </div>
                        <div className="space-y-2">
                          <Label htmlFor="subject">Subject *</Label>
                          <Input
                            id="subject"
                            name="subject"
                            value={formData.subject}
                            onChange={handleChange}
                            placeholder="How can we help?"
                            required
                            aria-required="true"
                          />
                        </div>
                      </div>

                      <div className="space-y-2">
                        <Label htmlFor="message">Message *</Label>
                        <Textarea
                          id="message"
                          name="message"
                          value={formData.message}
                          onChange={handleChange}
                          placeholder="Tell us more about your inquiry..."
                          rows={6}
                          required
                          aria-required="true"
                        />
                      </div>

                      <Button 
                        type="submit" 
                        variant="heritage" 
                        size="lg" 
                        className="w-full md:w-auto"
                        disabled={isSubmitting}
                      >
                        {isSubmitting ? (
                          <>
                            <div className="w-4 h-4 border-2 border-heritage-foreground/30 border-t-heritage-foreground rounded-full animate-spin mr-2" />
                            Sending...
                          </>
                        ) : (
                          <>
                            <Send className="w-4 h-4 mr-2" />
                            Send Message
                          </>
                        )}
                      </Button>
                    </form>
                  )}
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </section>

      {/* Map Section (Placeholder) */}
      <section className="py-16 px-4 sm:px-6 lg:px-8 bg-secondary/30">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-8">
            <h2 className="text-2xl font-bold text-foreground mb-4">Find Us</h2>
            <p className="text-muted-foreground">
              Visit us at our location or reach out using the contact information above.
            </p>
          </div>

          <div className="bg-muted rounded-lg overflow-hidden aspect-video max-w-4xl mx-auto flex items-center justify-center">
            <div className="text-center p-8">
              <MapPin className="w-16 h-16 text-muted-foreground/50 mx-auto mb-4" />
              <p className="text-muted-foreground">
                Map integration can be added here.<br />
                <a 
                  href={`https://maps.google.com/?q=${encodeURIComponent(getFullAddress(config))}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-heritage hover:underline"
                >
                  View on Google Maps →
                </a>
              </p>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Contact;
